// Upload queue helpers. The worker is the authority for progress
// events; this module only provides small utilities the main thread
// uses to (1) mint task ids, (2) convert a `File` to a bounded
// `ReadableStream<Uint8Array>` for transfer across Comlink, (3)
// drive a single `importFile` call end-to-end, and (4) generate +
// persist per-folder thumbnails for uploaded images and videos.
//
// No plaintext ever touches disk on the main-thread side: the file
// bytes flow File -> slice -> transferred ArrayBuffer -> worker.

import * as Comlink from "comlink";
import type { Remote } from "comlink";
import type { VaultWorkerApi } from "../worker/api";
import type { UploadTask } from "../stores/uploadQueue";
import { useUploadQueue } from "../stores/uploadQueue";
import { canThumbnail, makeThumbnail } from "./thumbnailGen";
import { canVideoThumbnail, makeVideoThumbnail } from "./videoThumbnailGen";
import { log } from "./log";

// Chunk size for File -> ReadableStream conversion. Keep modest so
// the main thread stays responsive while slicing; the worker
// re-chunks internally at the vault-core chunk boundary.
const SLICE_BYTES = 1 * 1024 * 1024; // 1 MiB

/**
 * Mint a unique task id of the shape `t_<base36>_<base36>`.
 *
 * First component: timestamp in ms. Second: 32 bits of randomness.
 * Collision-resistant enough for a single tab's upload queue.
 */
export function makeTaskId(): string {
  const ts = Date.now().toString(36);
  const rnd = Math.floor(Math.random() * 0x100000000).toString(36);
  return `t_${ts}_${rnd}`;
}

/**
 * Convert a `File` to a `ReadableStream<Uint8Array>` that emits
 * fixed-size slices. The returned stream is transferable across a
 * Comlink boundary with `Comlink.transfer`.
 */
export function fileToStream(file: File): ReadableStream<Uint8Array> {
  let offset = 0;
  return new ReadableStream<Uint8Array>({
    async pull(controller) {
      if (offset >= file.size) {
        controller.close();
        return;
      }
      const end = Math.min(offset + SLICE_BYTES, file.size);
      const slice = file.slice(offset, end);
      const buf = await slice.arrayBuffer();
      controller.enqueue(new Uint8Array(buf));
      offset = end;
    },
  });
}

/**
 * Register a pending upload in the queue store with status `queued`
 * and return its task id. Use this to surface every file in the
 * Transfers panel up front, before the sequential `runImport` loop
 * starts processing them one at a time.
 */
export function enqueueUpload(file: File): string {
  const taskId = makeTaskId();
  const task: UploadTask = {
    taskId,
    fileName: file.name,
    totalBytes: file.size,
    bytesDone: 0,
    status: "queued",
  };
  useUploadQueue.getState().enqueue(task);
  return taskId;
}

/**
 * Run a single upload to completion: flips the existing queued task
 * to `running`, kicks off thumbnail extraction in parallel with the
 * worker import (image or video), and on upload success persists the
 * thumbnail into the destination folder's `.thumbnails` sidecar file.
 *
 * Thumbnail extraction runs concurrently with the upload so the
 * chunk stream isn't delayed waiting for a video frame to decode.
 * A failed or absent thumbnail never fails the upload — the UI
 * just falls back to the file-type badge.
 *
 * `taskId` must come from a prior `enqueueUpload(file)` call.
 */
export async function runImport(
  vault: Remote<VaultWorkerApi>,
  handle: number,
  destFolder: string,
  file: File,
  taskId: string,
): Promise<void> {
  useUploadQueue.getState().update(taskId, { status: "running" });

  // Kick off thumbnail extraction in parallel with the upload. Both
  // `makeVideoThumbnail` and `makeThumbnail` resolve `null` on any
  // failure (never reject), so this promise is safe to hold without
  // an explicit catch.
  const thumbPromise: Promise<Uint8Array | null> = canVideoThumbnail(file.type)
    ? makeVideoThumbnail(file)
    : canThumbnail(file.type)
      ? makeThumbnail(file)
      : Promise.resolve(null);

  // As soon as the thumbnail resolves, surface it in the Transfers
  // panel via a blob URL. The store owns the URL's lifetime and
  // revokes it when the task leaves the queue. We don't await this
  // — the upload must not block on thumbnail decoding.
  thumbPromise
    .then((thumb) => {
      if (!thumb) return;
      const blob = new Blob([thumb as BlobPart], { type: "image/webp" });
      const thumbnailUrl = URL.createObjectURL(blob);
      useUploadQueue.getState().update(taskId, { thumbnailUrl });
    })
    .catch(() => {
      // Belt-and-braces: thumbPromise is contracted to resolve
      // `null` on failure, but a rendering glitch in Blob /
      // URL.createObjectURL must not throw an unhandled rejection.
    });

  // Worker's importFile appends `/${fileName}` to this path itself,
  // so pass the folder — not the joined file path — or the worker
  // would resolve `/foo.pdf/foo.pdf` and fail with path-not-found.
  const stream = fileToStream(file);
  try {
    await vault.importFile(
      handle,
      taskId,
      destFolder,
      file.name,
      file.type || "application/octet-stream",
      file.size,
      Comlink.transfer(stream, [stream]) as unknown as ReadableStream<Uint8Array>,
    );

    // Upload succeeded. Wait for the parallel thumbnail task to
    // settle before persisting the sidecar and marking the task
    // `done`, so the user never sees a completed upload that
    // briefly lacks its thumbnail.
    const thumbnail = await thumbPromise;
    if (thumbnail) {
      try {
        await updateThumbnails(vault, handle, destFolder, file.name, thumbnail);
      } catch (thumbErr) {
        // Sidecar write failed — upload itself is fine, so we still
        // mark the task `done` below. Log at warn; the redactor
        // strips any sensitive fields automatically.
        log.warn("thumbnail sidecar update failed", {
          fileName: file.name,
          error: String(thumbErr),
        });
      }
    }

    useUploadQueue.getState().update(taskId, {
      status: "done",
      bytesDone: file.size,
    });
  } catch (err) {
    const msg = String(err);
    // "path already exists" is a benign outcome — the vault already
    // has a file at that path, so this upload is a no-op from the
    // user's perspective. Mark it as skipped (muted UI) instead of
    // surfacing a red error, and don't rethrow so the caller's
    // per-file toast stays silent for this case.
    if (msg.includes("path already exists")) {
      useUploadQueue.getState().update(taskId, {
        status: "skipped",
        error: "Already in this folder",
      });
      return;
    }
    useUploadQueue.getState().update(taskId, {
      status: "error",
      error: msg,
    });
    throw err;
  }
}

async function updateThumbnails(
  vault: Remote<VaultWorkerApi>,
  handle: number,
  destFolder: string,
  fileName: string,
  webp: Uint8Array,
): Promise<void> {
  const { parseThumbnails, packThumbnails } = await import("./thumbnails");
  const thumbsPath = `${destFolder}/.thumbnails`.replace(/\/+/g, "/");

  // Read existing (if any), merge, rewrite. This read-modify-write is
  // safe because runImport is called sequentially per file from the
  // upload loop; there's no concurrent writer to race against.
  let existing = new Map<string, Uint8Array>();
  try {
    const rid = await vault.readerOpen(handle, thumbsPath);
    const chunks: Uint8Array[] = [];
    try {
      for (;;) {
        const c = await vault.readerNext(rid);
        if (c === null) break;
        chunks.push(c);
      }
    } finally {
      await vault.readerClose(rid);
    }
    const concat = concat1(chunks);
    if (concat.byteLength > 0) existing = parseThumbnails(concat);
  } catch {
    // File didn't exist — treat as empty.
  }

  existing.set(fileName, webp);
  const packed = packThumbnails(existing);

  // Delete old, then write fresh.
  try {
    await vault.deleteEntry(handle, thumbsPath);
  } catch {
    // may not exist
  }
  const wid = await vault.writerOpen(
    handle,
    thumbsPath,
    "application/octet-stream",
  );
  const CHUNK = 1 << 20;
  for (let i = 0; i < packed.byteLength; i += CHUNK) {
    await vault.writerPush(wid, packed.slice(i, i + CHUNK));
  }
  await vault.writerClose(wid);
}

function concat1(chunks: Uint8Array[]): Uint8Array {
  const total = chunks.reduce((s, c) => s + c.byteLength, 0);
  const out = new Uint8Array(total);
  let o = 0;
  for (const c of chunks) {
    out.set(c, o);
    o += c.byteLength;
  }
  return out;
}
